import { useState } from "react";
import { ArrowDown, ArrowUp, Minus, RefreshCw } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn, fromNowFR } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

/** Standalone variation badge for table columns. */
export function VariationBadge({ value }: { value: number | null | undefined }) {
  if (value == null) {
    return <span className="text-foreground-muted text-xs">—</span>;
  }
  const sign = Math.sign(value);
  const color = sign > 0 ? "text-emerald-500" : sign < 0 ? "text-red-500" : "text-foreground-muted";
  const Icon = sign > 0 ? ArrowUp : sign < 0 ? ArrowDown : Minus;
  return (
    <span className={cn("inline-flex items-center gap-0.5 tabular-nums text-sm", color)}>
      <Icon className="size-3" />
      {value > 0 ? "+" : ""}
      {value.toFixed(2)}%
    </span>
  );
}

interface Props {
  price: number | null | undefined;
  variation: number | null | undefined;
  currency?: string | null;
  /** When set, displays the "fetched X ago" hint + a refresh icon button. */
  fetchedAt?: string | null;
  /** When set, clicking the refresh icon triggers a price-only refresh of this forum. */
  forumId?: number | string;
  className?: string;
  size?: "sm" | "md";
  /** Hide the inline variation badge (e.g. when the table shows a separate Var. column). */
  hideVariation?: boolean;
}

const FR = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 4 });

export function PriceTag({
  price,
  variation,
  currency,
  fetchedAt,
  forumId,
  className,
  size = "sm",
  hideVariation = false,
}: Props) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [waitingForUpdate, setWaitingForUpdate] = useState(false);

  // Synchronous refresh : Boursorama is fetched inline by our Go API and the
  // fresh price comes back in the response. We immediately write it into the
  // React Query cache so the cell updates without re-fetching the whole list.
  const refresh = useMutation({
    mutationFn: () => {
      if (forumId == null) throw new Error("no forum id");
      return api.refreshForumPriceSync(forumId);
    },
    onSuccess: (data) => {
      if (forumId == null) return;
      // Patch every variant of the "forums" query cache so all rows render
      // the new value (we may have multiple, e.g. ["forums","all"] +
      // ["forums","hot"] in memory simultaneously).
      qc.setQueriesData({ queryKey: ["forums"] }, (prev: any) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((f: any) =>
          String(f.id) === String(forumId)
            ? {
                ...f,
                title: data.title || f.title,
                last_price: data.last_price ?? f.last_price,
                last_variation_pct: data.last_variation_pct ?? f.last_variation_pct,
                price_currency: data.price_currency ?? f.price_currency,
                isin: data.isin ?? f.isin,
                symbol: data.symbol ?? f.symbol,
                last_price_at: data.observed_at,
              }
            : f
        );
      });
      if (data.has_price) {
        toast.success(
          `Cours rafraîchi · ${data.last_price?.toFixed(2)}${data.price_currency ? " " + data.price_currency : ""}`
        );
      } else {
        toast.info("Pas de cotation pour ce forum");
      }
    },
    onError: (e) => toast.error(`Erreur : ${e instanceof Error ? e.message : "?"}`),
  });

  // Kept for the API surface but no longer needed (synchronous path).
  void setWaitingForUpdate;

  // Refresh hits an auth-gated endpoint (RequireUser) — only offer the scrape
  // button when the visitor is logged in.
  const canRefresh = isAuthenticated && forumId != null;

  if (price == null) {
    if (!canRefresh) {
      return <span className={cn("text-foreground-muted text-xs", className)}>—</span>;
    }
    return (
      <button
        onClick={() => refresh.mutate()}
        disabled={refresh.isPending || waitingForUpdate}
        className={cn(
          "inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground transition-colors",
          className
        )}
        title="Pas de cours connu — cliquer pour scraper"
      >
        <RefreshCw className={cn("size-3", (refresh.isPending || waitingForUpdate) && "animate-spin")} />
        <span>—</span>
      </button>
    );
  }
  const sign = variation == null ? 0 : Math.sign(variation);
  const color =
    sign > 0 ? "text-emerald-500" : sign < 0 ? "text-red-500" : "text-foreground-muted";
  const Icon = sign > 0 ? ArrowUp : sign < 0 ? ArrowDown : Minus;
  const txt = size === "md" ? "text-base" : "text-sm";
  const refreshing = refresh.isPending || waitingForUpdate;

  return (
    <div className={cn("inline-flex flex-col items-end leading-tight", className)}>
      <div className={cn("inline-flex items-center gap-1.5 tabular-nums", txt)}>
        <span className="font-medium">
          {FR.format(price)}
          {currency && (
            <span className="ml-0.5 text-foreground-muted text-[0.85em] font-normal">
              {currency}
            </span>
          )}
        </span>
        {variation != null && !hideVariation && (
          <span className={cn("inline-flex items-center gap-0.5", color)}>
            <Icon className="size-3" />
            {variation > 0 ? "+" : ""}
            {variation.toFixed(2)}%
          </span>
        )}
      </div>
      {(fetchedAt || canRefresh) && (
        <div className="flex items-center gap-1 text-[10px] text-foreground-muted mt-0.5">
          {fetchedAt && <span>{fromNowFR(fetchedAt)}</span>}
          {canRefresh && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                refresh.mutate();
              }}
              disabled={refreshing}
              className={cn(
                "rounded p-0.5 hover:bg-surface-muted disabled:opacity-50 transition-colors",
                refreshing && "text-primary"
              )}
              title="Re-récupérer le cours maintenant"
            >
              <RefreshCw className={cn("size-3", refreshing && "animate-spin")} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
